"use client";

import { THEME } from "@/excalidraw";
import { useEffect, useLayoutEffect, useState } from "react";
import { DRAW_STORAGE_KEYS, type DrawTheme } from "./constants";

const getDarkThemeMediaQuery = (): MediaQueryList | undefined =>
  typeof window !== "undefined" ? window.matchMedia?.("(prefers-color-scheme: dark)") : undefined;

export function useDrawTheme() {
  const [appTheme, setAppTheme] = useState<DrawTheme | "system">(() => {
    if (typeof window === "undefined") return THEME.LIGHT;
    return (
      (localStorage.getItem(DRAW_STORAGE_KEYS.THEME) as DrawTheme | "system" | null) ||
      THEME.LIGHT
    );
  });
  const [editorTheme, setEditorTheme] = useState<DrawTheme>(THEME.LIGHT);

  useEffect(() => {
    const mediaQuery = getDarkThemeMediaQuery();
    const handleChange = (e: MediaQueryListEvent) => {
      setEditorTheme(e.matches ? THEME.DARK : THEME.LIGHT);
    };
    if (appTheme === "system") {
      mediaQuery?.addEventListener("change", handleChange);
    }
    const handleKeydown = (event: KeyboardEvent) => {
      const ctrlOrCmd = event.ctrlKey || event.metaKey;
      if (!ctrlOrCmd && event.altKey && event.shiftKey && event.code === "KeyD") {
        event.preventDefault();
        event.stopImmediatePropagation();
        setAppTheme(editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK);
      }
    };
    document.addEventListener("keydown", handleKeydown, { capture: true });
    return () => {
      mediaQuery?.removeEventListener("change", handleChange);
      document.removeEventListener("keydown", handleKeydown, { capture: true });
    };
  }, [appTheme, editorTheme]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(DRAW_STORAGE_KEYS.THEME, appTheme);
    if (appTheme === "system") {
      setEditorTheme(getDarkThemeMediaQuery()?.matches ? THEME.DARK : THEME.LIGHT);
    } else {
      setEditorTheme(appTheme);
    }
  }, [appTheme]);

  return { editorTheme, appTheme, setAppTheme };
}
